import { atom, useRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "@/Functions";
import {
    EventService,
    EventListParams,
    CreateEventRequest,
    UpdateEventRequest,
    EventRegistrationListParams,
    Event
} from "@/Data/Admission";
import type { EventRegistration } from 'ai-api/types/admission';

// Main state atom for event data
const eventState = atom({
    key: 'eventState' + randomString(4),
    default: {
        events: {} as Record<string, Event>,
        registrations: {} as Record<string, EventRegistration>,
        loading: false,
        error: null as string | null,
    }
});

/**
 * Hook for managing admission events and registrations
 */
export const useAdmissionEvent = () => {
    const [state, setState] = useRecoilState(eventState);
    const overwriteState = useCallback((newState: Partial<typeof state>) => setState(prev => ({ ...prev, ...newState })), [setState]);

    const { events, registrations, loading, error } = state;

    // Fetch events from API
    const fetchEvents = useCallback(async (params?: EventListParams): Promise<void> => {
        overwriteState({ loading: true, error: null });

        try {
            const eventList = await EventService.listEvents(params);

            const eventsMap = eventList.reduce((acc, event) => {
                acc[event.eventid] = event;
                return acc;
            }, {} as Record<string, Event>);

            overwriteState({
                events: params?.eventid ? { ...state.events, ...eventsMap } : eventsMap,
                loading: false
            });
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to fetch events',
                loading: false
            });
        }
    }, [state.events, overwriteState]);

    // Fetch single event
    const fetchEvent = useCallback(async (eventid: string): Promise<Event | null> => {
        overwriteState({ loading: true, error: null });

        try {
            const event = await EventService.getEvent(eventid);
            if (event) {
                overwriteState({
                    events: { ...state.events, [event.eventid]: event },
                    loading: false
                });
            }
            return event;
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to fetch event',
                loading: false
            });
            return null;
        }
    }, [state.events, overwriteState]);

    // Create event (manager only)
    const createEvent = useCallback(async (request: CreateEventRequest): Promise<Event | null> => {
        overwriteState({ loading: true, error: null });

        try {
            const event = await EventService.createEvent(request);
            overwriteState({
                events: { ...state.events, [event.eventid]: event },
                loading: false
            });
            return event;
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to create event',
                loading: false
            });
            return null;
        }
    }, [state.events, overwriteState]);

    // Update event (manager only)
    const updateEvent = useCallback(async (request: UpdateEventRequest): Promise<Event | null> => {
        overwriteState({ loading: true, error: null });

        try {
            const event = await EventService.updateEvent(request);
            overwriteState({
                events: { ...state.events, [event.eventid]: event },
                loading: false
            });
            return event;
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to update event',
                loading: false
            });
            return null;
        }
    }, [state.events, overwriteState]);

    // Delete event (manager only)
    const deleteEvent = useCallback(async (eventid: string): Promise<boolean> => {
        overwriteState({ loading: true, error: null });

        try {
            await EventService.deleteEvent(eventid);
            const { [eventid]: deleted, ...remainingEvents } = state.events;
            overwriteState({
                events: remainingEvents,
                loading: false
            });
            return true;
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to delete event',
                loading: false
            });
            return false;
        }
    }, [state.events, overwriteState]);

    // Register for event
    const registerForEvent = useCallback(async (eventid: string): Promise<boolean> => {
        overwriteState({ loading: true, error: null });

        try {
            const registration = await EventService.registerForEvent(eventid);

            // Update event's registered status
            if (state.events[eventid]) {
                overwriteState({
                    events: {
                        ...state.events,
                        [eventid]: { ...state.events[eventid], registered: true }
                    },
                    registrations: { ...state.registrations, [eventid]: registration },
                    loading: false
                });
            }
            return true;
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to register for event',
                loading: false
            });
            return false;
        }
    }, [state.events, state.registrations, overwriteState]);

    // Unregister from event
    const unregisterFromEvent = useCallback(async (eventid: string): Promise<boolean> => {
        overwriteState({ loading: true, error: null });

        try {
            await EventService.unregisterFromEvent(eventid);

            // Update event's registered status
            const { [eventid]: deleted, ...remainingRegistrations } = state.registrations;

            if (state.events[eventid]) {
                overwriteState({
                    events: {
                        ...state.events,
                        [eventid]: { ...state.events[eventid], registered: false }
                    },
                    registrations: remainingRegistrations,
                    loading: false
                });
            }
            return true;
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to unregister from event',
                loading: false
            });
            return false;
        }
    }, [state.events, state.registrations, overwriteState]);

    // Fetch registrations
    const fetchRegistrations = useCallback(async (params?: EventRegistrationListParams): Promise<void> => {
        overwriteState({ loading: true, error: null });

        try {
            const registrationList = await EventService.listRegistrations(params);

            const registrationsMap = registrationList.reduce((acc, registration) => {
                acc[registration.eventid] = registration;
                return acc;
            }, {} as Record<string, EventRegistration>);

            overwriteState({
                registrations: registrationsMap,
                loading: false
            });
        } catch (err: any) {
            overwriteState({
                error: err.response?.data?.message || err.message || 'Failed to fetch registrations',
                loading: false
            });
        }
    }, [overwriteState]);

    // Helper functions
    const getEventsByUniversity = useCallback((universityid: string): Event[] => {
        return Object.values(events).filter(event => event.universityid === universityid);
    }, [events]);

    const getEventsByTrack = useCallback((trackid: string): Event[] => {
        return Object.values(events).filter(event => event.trackid === trackid);
    }, [events]);

    const getUpcomingEvents = useCallback((): Event[] => {
        const now = Date.now() / 1000;
        return Object.values(events)
            .filter(event => event.start_at > now && event.status === 'active')
            .sort((a, b) => a.start_at - b.start_at);
    }, [events]);

    const getRegisteredEvents = useCallback((): Event[] => {
        return Object.values(events).filter(event => event.registered === true);
    }, [events]);

    return {
        // State
        events,
        registrations,
        loading,
        error,

        // Actions
        fetchEvents,
        fetchEvent,
        createEvent,
        updateEvent,
        deleteEvent,
        registerForEvent,
        unregisterFromEvent,
        fetchRegistrations,

        // Selectors
        getEventsByUniversity,
        getEventsByTrack,
        getUpcomingEvents,
        getRegisteredEvents
    };
};